import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, CaretLeft, CaretRight, ArrowClockwise, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { unwrap, extractRows, extractTotal, pickId, BUSINESS_ID_KEYS } from "../lib/helpers";
import { DynamicTable } from "../components/DataViews";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

const SIZE = 25;

export default function Businesses() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/businesses", {
        params: { page, size: SIZE, q: search || undefined },
      });
      setData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = extractRows(data);
  const total = extractTotal(data, rows.length);
  const totalPages = Math.max(1, Math.ceil(total / SIZE));

  const openBusiness = (row) => {
    const id = pickId(row, BUSINESS_ID_KEYS);
    if (id) navigate(`/businesses/${encodeURIComponent(id)}`);
    else toast.error("No business id found on this row");
  };

  return (
    <div className="space-y-6" data-testid="businesses-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">Verified Businesses</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} business{total === 1 ? "" : "es"} on record. Select one to inspect its projects.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="businesses-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <form
        className="flex gap-2 max-w-xl"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
          setSearch(q.trim());
        }}
      >
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by email, WhatsApp number, business id or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="business-search-input"
          />
        </div>
        <Button type="submit" data-testid="business-search-button">Search</Button>
        {search && (
          <Button
            type="button"
            variant="ghost"
            data-testid="business-search-clear"
            onClick={() => {
              setQ("");
              setSearch("");
              setPage(0);
            }}
          >
            Clear
          </Button>
        )}
      </form>

      {loading ? (
        <div className="space-y-2" data-testid="businesses-loading">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : (
        <DynamicTable
          rows={rows}
          testId="businesses-table"
          onRowClick={openBusiness}
          empty="No businesses found for this query."
          renderActions={(row) => (
            <Button
              variant="ghost"
              size="icon"
              data-testid={`business-open-${pickId(row, BUSINESS_ID_KEYS) ?? "row"}`}
              onClick={(e) => {
                e.stopPropagation();
                openBusiness(row);
              }}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        />
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground font-data" data-testid="businesses-page-info">
          Page {page + 1} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="businesses-prev-page"
          >
            <CaretLeft className="mr-1 h-4 w-4" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || page + 1 >= totalPages || rows.length < SIZE}
            onClick={() => setPage((p) => p + 1)}
            data-testid="businesses-next-page"
          >
            Next <CaretRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
